/**
 * A releitura do histórico da conversa — um GET, uma fonte, dois chamadores.
 *
 * STO-214: o motor grava a resposta do turno incondicionalmente
 * (`turn-epilogue.ts`/`ConversationOrchestrator.ts`) e a devolve em
 * `GET /api/v1/conversations/:id`. O caminho de volta sempre funcionou — o que
 * faltava era QUEM chama. `ChatPage` só buscava ao trocar de conversa; nada
 * relia quando o app voltava do segundo plano ou quando o fio SSE morria com o
 * turno ainda em voo, e a resposta já gravada ficava presa no banco até a
 * PRÓXIMA abertura do app (medido: 62min50s de spinner morto na STO-214).
 *
 * Esta função é o ÚNICO lugar que faz esse GET — extraída do que era
 * `loadConversationHistory`, local a `ChatPage.tsx`, porque agora
 * `connectionStore.ts` também precisa dela para reconciliar um turno em voo
 * contra a verdade do servidor (ver `reconciliarTurnoEmVooComOServidor`).
 */
import { api, ApiError } from '@/lib/api'
import { dedupedGet } from '@/lib/request-dedupe'
import { stripSyntheticRailTurns } from '@/lib/synthetic-rail-turn'
import { rehydratePresentation } from '@/lib/persisted-presentation'
import { haRespostaDoServidorSemGemeoNaTela } from '@/lib/chat/historico-sem-duplicata'
import { useChatStore, type Message } from '@/stores/chatStore'

interface ApiMessage {
  id: string
  content: string
  role: 'user' | 'assistant'
  createdAt: string
  /**
   * RAIZ 8 (2026-07-25) — o envelope de apresentação do turno (card, botões,
   * chips) que o motor persiste em `Message.metadata` e devolve no histórico.
   * Sem este campo aqui, recarregar a página trazia só o texto: o card sumia e a
   * pergunta do onboarding voltava sem as opções, prendendo o usuário (M10).
   * `unknown` de propósito: o shape é validado em `persisted-presentation.ts`.
   */
  metadata?: unknown
}

export interface ReloadOutcome {
  /**
   * `true` quando o servidor devolveu uma mensagem do assistente que a tela
   * ainda não tinha. É o sinal que fecha o turno em voo com a resposta REAL,
   * em vez de deixar o indicador de "pensando" — ou um aviso fabricado no
   * cliente — por cima dela.
   */
  novaRespostaChegou: boolean
}

/**
 * Busca o histórico persistido da conversa e funde na tela (via
 * `ingestHistory`, tipicamente `chatStore.ingestConversationHistory` — já
 * despacha duplicata por papel+texto, ver `historico-sem-duplicata.ts`).
 *
 * Falha aberta: qualquer erro (rede, 401, corpo inesperado) devolve
 * `{ novaRespostaChegou: false }` em vez de propagar — quem chama nunca
 * precisa de try/catch próprio. 404 limpa o id obsoleto do localStorage, como
 * o comportamento original de `ChatPage`.
 */
export function reloadConversationHistory(
  conversationId: string,
  ingestHistory: (conversationId: string, messages: Message[]) => void,
): Promise<ReloadOutcome> {
  // O que a tela tinha ANTES do GET: é contra isto que se mede "resposta nova".
  // Só resposta FINALIZADA conta: a bolha em voo e a de erro são provisórias —
  // mesmo com o texto final inteiro, não fecham o turno (o servidor fecha).
  const naTelaAntes = useChatStore
    .getState()
    .messages.filter((m) => !m.isStreaming && m.status !== 'error')

  // #86/#74 — voo único, SEM memória: releituras simultâneas (troca de
  // conversa + reconciliação + aviso de importação) viram um GET só, e a
  // próxima releitura depois que ele volta continua indo ao banco.
  const caminho = `/api/v1/conversations/${conversationId}`
  return dedupedGet(
    caminho,
    () => api.get<{ data: { messages: ApiMessage[] }; messages?: ApiMessage[] }>(caminho),
    0,
  )
    .then((res) => {
      // Backend wraps in { data: { messages: [...] } }
      const msgs = res.data?.messages ?? (res as unknown as ApiMessage[])
      if (!Array.isArray(msgs) || !msgs.length) return { novaRespostaChegou: false }
      const kept = stripSyntheticRailTurns(msgs)
      if (import.meta.env.DEV && kept.length < msgs.length) {
        console.debug(
          `[reload-conversation] stripped ${msgs.length - kept.length} synthetic rail turn(s) from history`,
        )
      }
      // RAIZ 8 — reidrata o envelope persistido; só o ÚLTIMO turno do
      // assistente volta com os botões (rehydratePresentation congela os demais).
      const mapeadas: Message[] = rehydratePresentation(kept).map((m) => ({
        id: m.id,
        conversationId,
        content: m.content,
        ...(m.displayText ? { displayText: m.displayText } : {}),
        role: m.role,
        timestamp: new Date(m.createdAt).getTime(),
        status: 'sent' as const,
        ...(m.richContent !== undefined ? { richContent: m.richContent as never } : {}),
        ...(m.suggestedActions !== undefined
          ? { suggestedActions: m.suggestedActions as never }
          : {}),
        ...(m.webSearchMeta !== undefined ? { webSearchMeta: m.webSearchMeta as never } : {}),
        ...(m.metadata !== undefined ? { metadata: m.metadata as never } : {}),
      }))
      ingestHistory(conversationId, mapeadas)
      const novaRespostaChegou = haRespostaDoServidorSemGemeoNaTela(
        naTelaAntes,
        mapeadas,
        conversationId,
      )
      return { novaRespostaChegou }
    })
    .catch((err: unknown) => {
      // Limpa conversa obsoleta do localStorage se o backend retornar 404
      if (
        err instanceof ApiError &&
        err.status === 404 &&
        useChatStore.getState().activeConversationId === conversationId
      ) {
        console.warn(
          '[reload-conversation] Conversation not found, clearing stale ID:',
          conversationId,
        )
        useChatStore.getState().setActiveConversation(null)
        useChatStore.getState().setPendingConversation(true)
      }
      return { novaRespostaChegou: false }
    })
}
